import React, { useEffect, useId, useRef, useState } from "react";

type Option = { value: string; label: string };

export default function CodeAutocomplete({ label, placeholder, options, selection = "", children, onSelect, onClear, disabled = false }: {
  label: string; placeholder: string; options: Option[]; selection?: string; children?: React.ReactNode;
  onSelect: (value: string) => void; onClear?: () => void; disabled?: boolean;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const matches = options.filter((option) => option.value === "other" || option.label.toLowerCase().includes(query.trim().toLowerCase()));
  const activeIndex = Math.min(active, matches.length - 1);
  useEffect(() => {
    if (open) list.current?.children[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  function choose(value: string) {
    onSelect(value);
    setQuery(""); setOpen(false); setActive(0);
  }

  return <div className="relative min-w-0" onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) { setOpen(false); setQuery(""); }
  }}>
    <label htmlFor={id} className="block text-sm font-medium">{label}</label>
    <div className="mt-1 flex min-h-10 flex-wrap items-center gap-1 rounded-lg border border-neutral-300 bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-emerald-600 dark:border-neutral-700 dark:bg-neutral-950">
      {children}
      <input id={id} role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={`${id}-options`}
        aria-activedescendant={open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
        autoComplete="off" disabled={disabled} placeholder={placeholder} title={selection || undefined} value={open ? query : selection}
        className="min-w-24 flex-1 border-0 bg-transparent px-1 py-1 text-sm text-neutral-950 outline-none focus:ring-0 dark:text-white"
        onFocus={() => { setOpen(true); setQuery(""); setActive(0); }}
        onClick={() => { if (!open) { setOpen(true); setQuery(""); setActive(0); } }}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault(); setOpen(true);
            setActive(open ? Math.max(0, Math.min(matches.length - 1, activeIndex + (event.key === "ArrowDown" ? 1 : -1))) : 0);
          } else if (event.key === "Enter") {
            event.preventDefault();
            if (open && matches[activeIndex]) choose(matches[activeIndex].value);
          } else if (event.key === "Escape") {
            event.preventDefault(); setOpen(false); setQuery("");
          }
        }} />
      {onClear && selection && <button type="button" aria-label={`Clear ${label.toLowerCase()}`} className="px-1 text-neutral-500 hover:text-red-700" onClick={() => { onClear(); setQuery(""); setOpen(false); }}>×</button>}
    </div>
    {open && !disabled && <ul ref={list} id={`${id}-options`} role="listbox" aria-label={`${label} suggestions`} className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
      {matches.map((option, index) => <li id={`${id}-option-${index}`} key={option.value} role="option" aria-selected={index === activeIndex}
        className={`cursor-pointer px-3 py-2 text-sm ${index === activeIndex ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100" : "text-neutral-900 dark:text-neutral-100"}`}
        onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(option.value)}>{option.label}</li>)}
    </ul>}
  </div>;
}
